import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Typography, Button, CircularProgress, Pagination, IconButton, Dialog, Tabs, Tab, Paper, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
// Import your API action to fetch funds
import { GetFundList } from '../../Redux/Actions/strategyService';
import { getOnboardedInvestors, getInvestorsByEmail } from '../../Redux/Actions/transactionService';
import { ApiCall, Base_URL } from '../../Common/Path';
import FundDetails from "./FundDetails";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

function getInitials(name) {
    if (!name) return '';
    const words = name.trim().split(' ');
    if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

const FundList = ({ firmId, firmName, onBack }) => {
    const dispatch = useDispatch();
    const { onboardedInvestors, onboardedInvestorsLoading, investorsByEmail, investorsByEmailLoading } = useSelector(state => state.transaction);

    // Suppress ResizeObserver loop errors
    useEffect(() => {
        const handleResizeObserverError = (e) => {
            if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
                e.stopImmediatePropagation();
                e.preventDefault();
                return false;
            }
        };
        
        const handleUnhandledRejection = (e) => {
            if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
                e.preventDefault();
                return false;
            }
        };
        
        window.addEventListener('error', handleResizeObserverError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        
        // Override console.error to suppress ResizeObserver errors
        const originalConsoleError = console.error;
        console.error = (...args) => {
            if (args[0] && args[0].includes && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
                return;
            }
            originalConsoleError.apply(console, args);
        };
        
        return () => {
            window.removeEventListener('error', handleResizeObserverError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
            console.error = originalConsoleError;
        };
    }, []);
    const [funds, setFunds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedFund, setSelectedFund] = useState(null);
    const [investorsModalOpen, setInvestorsModalOpen] = useState(false);
    const [selectedInvestorsFund, setSelectedInvestorsFund] = useState(null);
    const [showFundDetailsWithManageTab, setShowFundDetailsWithManageTab] = useState(false);
    const [modalInvestorsLoading, setModalInvestorsLoading] = useState(false);
    const [modalInvestors, setModalInvestors] = useState([]);
    const itemsPerPage = 10;

    // Tab state
    const [activeTab, setActiveTab] = useState(0);
    
    // Investor list state
    const [allInvestors, setAllInvestors] = useState([]);
    const [investorsLoading, setInvestorsLoading] = useState(false);
    const [investorSearchTerm, setInvestorSearchTerm] = useState('');
    const [investorSortBy, setInvestorSortBy] = useState('name');
    const [investorSortOrder, setInvestorSortOrder] = useState('asc'); // 'asc' or 'desc'
    const [investorPage, setInvestorPage] = useState(1);
    const [investorRowsPerPage, setInvestorRowsPerPage] = useState(7);
    const [investorTotalPages, setInvestorTotalPages] = useState(1);

    // Create Account Linkage Modal state
    const [linkageModalOpen, setLinkageModalOpen] = useState(false);
    const [searchEmail, setSearchEmail] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showCreateAccount, setShowCreateAccount] = useState(false);
    const [newAccountType, setNewAccountType] = useState('');
    const [emailError, setEmailError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    // NAV matches (from multi-API integration)
    const [navMatchesLoading, setNavMatchesLoading] = useState(false);
    const [matchedNavAccounts, setMatchedNavAccounts] = useState([]); // [{name, fund, investorId, fundId}]
    const [isCreatingNewAccount, setIsCreatingNewAccount] = useState(false);
    const [createAccountLoading, setCreateAccountLoading] = useState(false);
    const [inlineAlertType, setInlineAlertType] = useState(''); // 'success' | 'error'
    const [inlineAlertMessage, setInlineAlertMessage] = useState('');

    // Link Accounts Modal state
    const [linkAccountsModalOpen, setLinkAccountsModalOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedNavAccounts, setSelectedNavAccounts] = useState([]);
    const [linkSaving, setLinkSaving] = useState(false);
    const [linkAlertType, setLinkAlertType] = useState(''); // 'success' | 'error'
    const [linkAlertMessage, setLinkAlertMessage] = useState('');

    // View Linkage Modal state
    const [viewLinkageModalOpen, setViewLinkageModalOpen] = useState(false);
    const [selectedLinkageData, setSelectedLinkageData] = useState(null);
    const [unlinkAlertType, setUnlinkAlertType] = useState(''); // 'success' | 'error'
    const [unlinkAlertMessage, setUnlinkAlertMessage] = useState('');
    
    // Unlink Confirmation Modal state
    const [unlinkConfirmModalOpen, setUnlinkConfirmModalOpen] = useState(false);

    // Ref for managing form visibility smoothly
    const formContainerRef = useRef(null);

    // Set of already linked (fundId:investorId) pairs for the selected user role (from onboarded investors)
    const linkedInvestorPairsSet = React.useMemo(() => {
        try {
            if (!selectedAccount || !selectedAccount.id) return new Set();
            const roleId = String(selectedAccount.id);
            const row = Array.isArray(onboardedInvestors)
                ? onboardedInvestors.find(r => r?.userRole?.id === roleId)
                : null;
            if (!row || !Array.isArray(row.funds)) return new Set();
            const pairs = new Set();
            row.funds.forEach(f => {
                const fundId = f?.fundId != null ? String(f.fundId) : '';
                if (Array.isArray(f?.investorIds)) {
                    f.investorIds.forEach(iid => {
                        const investorId = iid != null ? String(iid) : '';
                        if (fundId && investorId) {
                            pairs.add(`${fundId}:${investorId}`);
                        }
                    });
                }
            });
            return pairs;
        } catch (e) {
            return new Set();
        }
    }, [onboardedInvestors, selectedAccount]);

    // Total linked accounts for this user role (unique fundId:investorId pairs)
    const linkedCount = linkedInvestorPairsSet.size;

    // Debug: Log when linkedInvestorPairsSet changes
    // React.useEffect(() => {
    //     if (selectedAccount && linkAccountsModalOpen) {
    //         const roleId = String(selectedAccount.id);
    //         const row = Array.isArray(onboardedInvestors)
    //             ? onboardedInvestors.find(r => r?.userRole?.id === roleId)
    //             : null;
    //     }
    // }, [linkedInvestorPairsSet, selectedAccount, linkAccountsModalOpen, onboardedInvestors, linkedCount]);

    useEffect(() => {
        setLoading(true);
        // Real API call to get funds by firm ID
        dispatch(GetFundList({ id: firmId, enrich: true }))
            .then((response) => {
                if (response && response?.payload.statusCode === 200) {
                    setFunds(response?.payload.data || []);
                    setTotalPages(Math.ceil((response?.payload.data?.length || 0) / itemsPerPage));
                } else {
                    setFunds([]);
                    setTotalPages(1);
                }
            })
            .catch((error) => {
                return error;
                // setFunds([]);
                // setTotalPages(1);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [dispatch, firmId]);

    useEffect(() => {
        if (investorsModalOpen && selectedInvestorsFund) {
            setModalInvestorsLoading(true);
            const token = localStorage.getItem('token');
            fetch(`https://prime-be-dev.parilabs.xyz/accounting/firms/${firmId}/funds/${selectedInvestorsFund.id}/investors`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            })
            .then(async res => {
                if (res.status === 304) {
                    setModalInvestors([]);
                    return;
                }
                const text = await res.text();
                try {
                    const data = JSON.parse(text);
                    if (Array.isArray(data.data) && data.data.length > 0) {
                        setModalInvestors(data.data);
                    } else {
                        setModalInvestors([]);
                       
                    }
                } catch (e) {
                    setModalInvestors([]);
                   
                }
            })
            .catch((err) => {
                setModalInvestors([]);
               
            })
            .finally(() => setModalInvestorsLoading(false));
        }
    }, [investorsModalOpen, selectedInvestorsFund, firmId]);

    // Fetch onboarded investors for the Investor List tab
    useEffect(() => {
        if (activeTab === 1 && firmId) {
            // Fetch all investors without email filter for client-side search
            dispatch(getOnboardedInvestors({ 
                firmId: firmId, 
                email: '' // Empty string to get all investors
            }));
        }
    }, [activeTab, firmId, dispatch]);

    // Update local state when Redux state changes
    useEffect(() => {
        const flattenOnboarded = (data) => {
            if (!Array.isArray(data)) return [];
            const rows = [];
            data.forEach((item) => {
                const roleId = item?.userRole?.id ? String(item.userRole.id) : '';
                const first = item?.userRole?.user?.userInfo?.firstName || '';
                const last = item?.userRole?.user?.userInfo?.lastName || '';
                const name = `${first} ${last}`.trim() || item?.userRole?.user?.email || 'Unknown Investor';
                const email = item?.userRole?.user?.email || '';
                const funds = Array.isArray(item?.funds) ? item.funds : [];
                if (funds.length === 0) {
                    rows.push({ id: `${roleId}-no-fund`, name, email, fundId: '', investorId: '', userRoleId: roleId, onboarded: true });
                } else {
                    funds.forEach((f) => {
                        const fundId = f?.fundId != null ? String(f.fundId) : '';
                        const invIds = Array.isArray(f?.investorIds) ? f.investorIds : [];
                        if (invIds.length === 0) {
                            rows.push({ id: `${roleId}-${fundId}-empty`, name, email, fundId, investorId: '', userRoleId: roleId, onboarded: true });
                        } else {
                            invIds.forEach((iid, idx) => {
                                rows.push({ id: `${roleId}-${fundId}-${String(iid)}-${idx}` , name, email, fundId, investorId: String(iid), userRoleId: roleId, onboarded: true });
                            });
                        }
                    });
                }
            });
            return rows;
        };

        if (onboardedInvestors && Array.isArray(onboardedInvestors)) {
            const flat = flattenOnboarded(onboardedInvestors);
            setAllInvestors(flat);
            setInvestorTotalPages(Math.ceil(flat.length / investorRowsPerPage));
        } else {
            setAllInvestors([]);
            setInvestorTotalPages(1);
        }
        setInvestorsLoading(onboardedInvestorsLoading);
    }, [onboardedInvestors, onboardedInvestorsLoading, investorRowsPerPage]);

    // Handle investors by email API response (map new API shape to UI-friendly list)
    useEffect(() => {
        if (Array.isArray(investorsByEmail)) {
            const mapped = [];
            investorsByEmail.forEach((user) => {
                const fullName = user?.userInfo ? `${user.userInfo.firstName || ''} ${user.userInfo.lastName || ''}`.trim() : '';
                const displayName = fullName || user?.email || 'Unknown';
                const roles = Array.isArray(user?.userRoles) ? user.userRoles : [];
                if (roles.length > 0) {
                    roles.forEach((role) => {
                        if (role?.role === 'INVESTOR') {
                            mapped.push({
                                id: role?.id || user?.id,
                                accountName: displayName,
                                accountType: role?.investorType || 'UNKNOWN',
                                email: user?.email || '',
                            });
                        }
                    });
                } else {
                    mapped.push({
                        id: user?.id,
                        accountName: displayName,
                        accountType: 'UNKNOWN',
                        email: user?.email || '',
                    });
                }
            });
            setSearchResults(mapped);
        } else if (investorsByEmail !== undefined && investorsByEmail !== null) {
            // Fallback if API returns single object
            setSearchResults([]);
        }
        setSearchLoading(investorsByEmailLoading);
    }, [investorsByEmail, investorsByEmailLoading]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    // Get current page items
    const getCurrentPageItems = () => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return funds.slice(startIndex, endIndex);
    };

    const handleViewFundDetails = (fund) => {
        setSelectedFund(fund);
    };

    const handleBackToFundList = () => {
        setSelectedFund(null);
    };

    const handleOpenInvestorsModal = (fund) => {
        setSelectedInvestorsFund(fund);
        setInvestorsModalOpen(true);
    };

    const handleCloseInvestorsModal = () => {
        setInvestorsModalOpen(false);
        setSelectedInvestorsFund(null);
    };

    const handleOpenManageInvestorsTab = () => {
        setShowFundDetailsWithManageTab(true);
        setInvestorsModalOpen(false);
        setSelectedFund(selectedInvestorsFund);
    };

    // Tab handlers
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Investor list handlers
    const handleInvestorSearchChange = (event) => {
        setInvestorSearchTerm(event.target.value);
        setInvestorPage(1); // Reset to first page when searching
        // The API call will be triggered by the useEffect that watches investorSearchTerm
    };

    // const handleInvestorSortChange = (event) => {
    //     setInvestorSortBy(event.target.value);
    // };

    const handleToggleSortOrder = () => {
        setInvestorSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
    };

    const handleInvestorPageChange = (event, value) => {
        setInvestorPage(value);
    };

    const handleInvestorRowsPerPageChange = (event) => {
        setInvestorRowsPerPage(parseInt(event.target.value));
        setInvestorPage(1);
    };

    // Get filtered and paginated investors
    const getFilteredInvestors = () => {
        // Always work on a cloned array to avoid mutating any frozen/immutable arrays from Redux
        let filtered = Array.isArray(allInvestors) ? [...allInvestors] : [];

        // Filter by search term (search in name, email, and fundId)
        if (investorSearchTerm && investorSearchTerm.trim()) {
            const searchLower = investorSearchTerm.toLowerCase().trim();
            filtered = filtered.filter(inv => {
                const email = (inv.InvestorEmail || inv.email || '').toLowerCase();
                const name = (inv.InvestorName || inv.name || '').toLowerCase();
                const fundId = (inv.fundId || '').toString().toLowerCase();
                
                return email.includes(searchLower) || 
                       name.includes(searchLower) ||
                       fundId.includes(searchLower);
            });
        }

        // Sort on the cloned array
        filtered.sort((a, b) => {
            if (investorSortBy === 'name') {
                const nameA = (a.InvestorName || a.name || '').toLowerCase();
                const nameB = (b.InvestorName || b.name || '').toLowerCase();
                const comparison = nameA.localeCompare(nameB);
                return investorSortOrder === 'asc' ? comparison : -comparison;
            }
            return 0;
        });

        return filtered;
    };

    const getCurrentPageInvestors = () => {
        const filtered = getFilteredInvestors();
        const startIndex = (investorPage - 1) * investorRowsPerPage;
        const endIndex = startIndex + investorRowsPerPage;
        return filtered.slice(startIndex, endIndex);
    };

    // Create Account Linkage Modal handlers
    const handleOpenLinkageModal = () => {
        setLinkageModalOpen(true);
    };

    const handleCloseLinkageModal = () => {
        setLinkageModalOpen(false);
        setSearchEmail('');
        setSearchResults([]);
        setShowCreateAccount(false);
        setNewAccountType('');
        setEmailError('');
        setHasSearched(false);
        setIsCreatingNewAccount(false);
        setCreateAccountLoading(false);
    };

    // Email validation function
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSearchInvestor = () => {
        // Validate email format
        if (!searchEmail.trim()) {
            setEmailError('Please enter an email address');
            return;
        }

        if (!validateEmail(searchEmail.trim())) {
            setEmailError('Please enter a valid email address');
            return;
        }

        setEmailError('');
        setHasSearched(true);
        
        // Call the API for user roles by email only
        dispatch(getInvestorsByEmail({ firmId, email: searchEmail.trim() }));
    };

    // Build NAV matches using the new single API: investors/by-email-and-funds
    const buildNavMatches = async (email) => {
        try {
            setNavMatchesLoading(true);
            setMatchedNavAccounts([]);

            // Use the new single API that replaces the 3 old APIs:
            // - onboarded-investors?email
            // - funds-by-email?email  
            // - investors-by-email?email
            const url = `${Base_URL}/accounting/firms/${firmId}/investors/by-email-and-funds?email=${encodeURIComponent(email)}`;
            const response = await ApiCall(url, "", "get");
            
            // Extract data from response
            const data = (response && response.statusCode === 200 && Array.isArray(response.data)) 
                ? response.data 
                : [];

            // Map the new API response structure to the expected format
            // New API returns: InvestorName, InvestorID, FundName, GlobalFundID, InvestorClassID, etc.
            const rows = data.map((item) => {
                return {
                    name: item.InvestorName || 'Unknown Investor',
                    fund: item.FundName || 'Unknown Fund',
                    investorId: item.InvestorID || item.InvestorId || item.id || '',
                    fundId: String(item.GlobalFundID || item.fundId || item.id || ''),
                    investorClassID: item.InvestorClassID,
                    investorNumber: item.InvestorNumber,
                    investmentStartDate: item.InvestmentStartDate,
                    investmentDailyAccountingLastAvailableDate: item.InvestmentDailyAccountingLastAvailableDate,
                    investmentOfficialAccountingLastAvailableDate: item.InvestmentOfficialAccountingLastAvailableDate
                };
            });

            setMatchedNavAccounts(rows);
        } catch (e) {
            console.error('Error building NAV matches:', e);
            setMatchedNavAccounts([]);
        } finally {
            setNavMatchesLoading(false);
        }
    };

    const handleLinkNAVAccounts = (accountId) => {
        // Find the account and open the Link Accounts modal
        const account = searchResults.find(acc => acc.id === accountId);
        if (account) {
            setSelectedAccount(account);
            setLinkAccountsModalOpen(true);
            setSelectedNavAccounts([]);
            // Fire the 3-API integration using the account's email (fallback to current searchEmail)
            const emailToUse = (account && account.email) ? account.email : searchEmail;
            if (emailToUse) {
                buildNavMatches(emailToUse.trim());
            }
            // Refresh onboarded investors to get latest linked data
            if (firmId) {
                dispatch(getOnboardedInvestors({ firmId, email: emailToUse.trim() }));
            }
            // Hide the first modal when opening the second modal
            setLinkageModalOpen(false);
        }
    };

    const handleCloseLinkAccountsModal = () => {
        setLinkAccountsModalOpen(false);
        setSelectedAccount(null);
        setSelectedNavAccounts([]);
        // Reopen the first modal when closing the second modal
        setLinkageModalOpen(true);
        setLinkSaving(false);
        setLinkAlertType('');
        setLinkAlertMessage('');
    };

    const handleNavAccountToggle = (navAccountId) => {
        setSelectedNavAccounts(prev => {
            if (prev.includes(navAccountId)) {
                return prev.filter(id => id !== navAccountId);
            } else {
                return [...prev, navAccountId];
            }
        });
    };

    const handleSaveLinkages = async () => {
        if (!selectedAccount || !selectedAccount.id) {
            setLinkAlertType('error');
            setLinkAlertMessage('Missing selected user role.');
            return;
        }
        if (selectedNavAccounts.length === 0) {
            setLinkAlertType('error');
            setLinkAlertMessage('Please select at least one NAV account.');
            return;
        }

        // Build payload from selected checkboxes and deduplicated accounts
        // First, deduplicate matchedNavAccounts the same way as in rendering
        const uniqueAccountsMap = new Map();
        matchedNavAccounts.forEach(acc => {
            const fundIdStr = acc.fundId != null ? String(acc.fundId) : '';
            const invIdStr = acc.investorId != null ? String(acc.investorId) : '';
            const pairKey = fundIdStr && invIdStr ? `${fundIdStr}:${invIdStr}` : '';
            if (pairKey && !uniqueAccountsMap.has(pairKey)) {
                uniqueAccountsMap.set(pairKey, acc);
            }
        });
        const uniqueAccounts = Array.from(uniqueAccountsMap.values());

        // Build payload from selected checkboxes
        const byFund = new Map();
        uniqueAccounts.forEach((item, index) => {
            const key = `nav-${item.fundId}-${item.investorId}-${index}`;
            if (selectedNavAccounts.includes(key)) {
                const list = byFund.get(String(item.fundId)) || [];
                list.push(String(item.investorId));
                byFund.set(String(item.fundId), list);
            }
        });
        const funds = Array.from(byFund.entries()).map(([fundId, investorIds]) => ({ fundId, investorIds }));

        if (funds.length === 0) {
            setLinkAlertType('error');
            setLinkAlertMessage('Could not build linkage payload. Please reselect items.');
            return;
        }

        const payload = {
            userRoleId: String(selectedAccount.id),
            funds
        };

        try {
            setLinkSaving(true);
            setLinkAlertType('');
            setLinkAlertMessage('');
            const url = `${Base_URL}/accounting/firms/${firmId}/assign`;
            const res = await ApiCall(url, payload, 'post');
            const ok = res && (res.statusCode === 200 || res.statusCode === 201 || res.success === true);
            if (ok) {
                setLinkAlertType('success');
                setLinkAlertMessage(res.message || 'Linkage saved successfully');
                // refresh lists with the correct email
                const emailToUse = (selectedAccount && selectedAccount.email) ? selectedAccount.email : searchEmail;
                if (firmId && emailToUse) {
                    // Refresh onboarded investors to get updated linked data
                    await dispatch(getOnboardedInvestors({ firmId, email: emailToUse.trim() }));
                    // Refresh search results
                    dispatch(getInvestorsByEmail({ firmId, email: emailToUse.trim() }));
                    // Rebuild NAV matches to show updated linked status
                    await buildNavMatches(emailToUse.trim());
                }
                // close after short delay
                setTimeout(() => {
                    handleCloseLinkAccountsModal();
                }, 1500);
            } else {
                setLinkAlertType('error');
                setLinkAlertMessage((res && (res.message || res.error)) || 'Failed to save linkage');
            }
        } catch (err) {
            setLinkAlertType('error');
            setLinkAlertMessage((err && err.message) || 'Failed to save linkage');
        } finally {
            setLinkSaving(false);
        }
    };

    const handleSendInvite = () => {
        setIsCreatingNewAccount(false);
        setShowCreateAccount(true);
    };

    const handleAccountTypeChange = (event) => {
        setNewAccountType(event.target.value);
    };

    const handleCreateAccount = () => {
        // First click: switch to "Create New Account" heading, no API yet
        if (!isCreatingNewAccount) {
            setIsCreatingNewAccount(true);
            return;
        }

        // Second click (Continue): validate and call API
        if (!searchEmail || !newAccountType) {
            setInlineAlertType('error');
            setInlineAlertMessage('Please enter a valid email and select account type.');
            return;
        }
        const payload = {
            email: searchEmail.trim(),
            role: 'INVESTOR',
            investorType: newAccountType,
        };
        setCreateAccountLoading(true);
        ApiCall(`${Base_URL}/users`, payload, 'post')
            .then((res) => {
                if (!res) {
                    setInlineAlertType('error');
                    setInlineAlertMessage('No response from server');
                    return;
                }
                const ok = res.statusCode === 200 || res.statusCode === 201 || res.success === true;
                if (ok) {
                    setInlineAlertType('success');
                    setInlineAlertMessage(res.message || 'Account invite created successfully');
                    if (firmId) {
                        dispatch(getOnboardedInvestors({ firmId, email: investorSearchTerm }));
                    }
                    if (searchEmail.trim()) {
                        dispatch(getInvestorsByEmail({ firmId, email: searchEmail.trim() }));
                    }
                    setTimeout(() => {
                        setSearchEmail('');
                        setSearchResults([]);
                        setShowCreateAccount(false);
                        setNewAccountType('');
                        setEmailError('');
                        setHasSearched(false);
                        setIsCreatingNewAccount(false);
                        setInlineAlertType('');
                        setInlineAlertMessage('');
                    }, 1200);
                } else {
                    const msg = res.message || res.error || 'Failed to create/invite user';
                    setInlineAlertType('error');
                    setInlineAlertMessage(msg);
                }
            })
            .catch((err) => {
                const msg = (err && err.message) || 'Request failed';
                setInlineAlertType('error');
                setInlineAlertMessage(msg);
            })
            .finally(() => {
                setCreateAccountLoading(false);
            });
    };

    const handleClearSearch = () => {
        setSearchEmail('');
        setSearchResults([]);
        setEmailError('');
        setHasSearched(false);
        setShowCreateAccount(false);
        setNewAccountType('');
        setIsCreatingNewAccount(false);
        setCreateAccountLoading(false);
        setInlineAlertType('');
        setInlineAlertMessage('');
    };

    const handleCreateAccountClick = useCallback(() => {
        // Use a more robust approach to avoid ResizeObserver loop error
        const updateState = () => {
            setIsCreatingNewAccount(true);
            // Temporarily disable ResizeObserver on the form container
            if (formContainerRef.current) {
                const resizeObserver = formContainerRef.current._resizeObserver;
                if (resizeObserver) {
                    resizeObserver.disconnect();
                }
            }
            
            setShowCreateAccount(true);
            
            // Re-enable ResizeObserver after a short delay
            setTimeout(() => {
                if (formContainerRef.current && formContainerRef.current._resizeObserver) {
                    formContainerRef.current._resizeObserver.observe(formContainerRef.current);
                }
            }, 100);
        };
        
        // Use multiple fallback strategies
        requestAnimationFrame(() => {
            setTimeout(updateState, 0);
        });
    }, []);

    const handleViewLinkage = (investor) => {
        // Find the fund name
        const fundId = investor.fundId ? String(investor.fundId) : '';
        const fund = funds.find(f => String(f.id) === fundId);
        const fundName = fund ? fund.name : (fundId || 'Unknown Fund');
        
        // Find all Prime accounts linked to this NAV investor
        const roleId = investor.userRoleId || '';
        const investorId = investor.investorId ? String(investor.investorId) : '';
        
        // Find all onboarded items that match this userRole.id
        // The API response shows funds can be null, so we need to handle that
        const matchingOnboardedItems = Array.isArray(onboardedInvestors)
            ? onboardedInvestors.filter(item => {
                // Match by userRole.id - this is the primary match
                return item?.userRole?.id === roleId;
            })
            : [];
        
        const primeAccounts = [];
        let assignmentId = '';
        let userEmail = '';
        
        // Process all matching onboarded items to build prime accounts list
        matchingOnboardedItems.forEach(onboardedItem => {
            if (onboardedItem && onboardedItem.userRole) {
                const userInfo = onboardedItem.userRole.user?.userInfo;
                const accountName = userInfo 
                    ? `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() 
                    : onboardedItem.userRole.user?.email || 'Unknown';
                const accountType = onboardedItem.userRole.investorType || 'UNKNOWN';
                const itemEmail = onboardedItem.userRole.user?.email || '';
                
                // Get the assignment ID from the item's id field
                const itemAssignmentId = onboardedItem.id || '';
                
                // If we have a fundId and funds array exists, try to find the assignment that links to this fund
                // Otherwise, use the first matching assignment
                if (!assignmentId) {
                    if (fundId && Array.isArray(onboardedItem.funds) && onboardedItem.funds.length > 0) {
                        // Check if this assignment has the fund linked
                        const fundMatch = onboardedItem.funds.find(f => {
                            const fFundId = f?.fundId != null ? String(f.fundId) : '';
                            const matchesFund = fFundId === fundId;
                            if (matchesFund && Array.isArray(f?.investorIds)) {
                                return f.investorIds.some(iid => String(iid) === investorId);
                            }
                            return matchesFund;
                        });
                        if (fundMatch) {
                            assignmentId = itemAssignmentId;
                        }
                    } else {
                        // If funds is null or empty, use this assignment ID
                        // This handles the case where the API returns funds: null
                        assignmentId = itemAssignmentId;
                    }
                }
                
                // Use email from first item if not set
                if (!userEmail) {
                    userEmail = itemEmail;
                }
                
                primeAccounts.push({
                    name: accountName,
                    type: accountType,
                    email: itemEmail,
                    assignmentId: itemAssignmentId
                });
            }
        });
        
        // If we still don't have an assignmentId but have matching items, use the first one
        if (!assignmentId && matchingOnboardedItems.length > 0) {
            assignmentId = matchingOnboardedItems[0].id || '';
        }
        
        // Log for debugging
        console.log('handleViewLinkage - Data extracted:', {
            investor,
            fundId,
            roleId,
            investorId,
            matchingItemsCount: matchingOnboardedItems.length,
            onboardedItems: matchingOnboardedItems.map(item => ({
                id: item.id,
                hasFunds: Array.isArray(item.funds),
                fundsLength: Array.isArray(item.funds) ? item.funds.length : 'null',
                userRoleId: item?.userRole?.id
            })),
            extractedAssignmentId: assignmentId,
            extractedFundId: fundId,
            primeAccountsCount: primeAccounts.length
        });
        
        setSelectedLinkageData({
            navAccount: {
                name: investor.name || 'Unknown Investor',
                fund: fundName,
                investorId: investorId
            },
            primeAccounts: primeAccounts,
            assignmentId: assignmentId,
            fundId: fundId,
            email: userEmail
        });
        setViewLinkageModalOpen(true);
    };

    const handleCloseViewLinkageModal = () => {
        setViewLinkageModalOpen(false);
        setSelectedLinkageData(null);
        setUnlinkAlertType('');
        setUnlinkAlertMessage('');
        setUnlinkConfirmModalOpen(false);
    };

    const handleOpenUnlinkConfirmation = () => {
        setUnlinkConfirmModalOpen(true);
    };

    const handleCloseUnlinkConfirmation = () => {
        setUnlinkConfirmModalOpen(false);
    };

    const handleUnlinkAccount = async () => {
        // Validate that we have the required data
        if (!selectedLinkageData) {
            console.error('selectedLinkageData is null or undefined');
            setUnlinkAlertType('error');
            setUnlinkAlertMessage('Missing required data to unlink account');
            setUnlinkConfirmModalOpen(false);
            return;
        }

        const assignmentId = selectedLinkageData.assignmentId;
        const fundId = selectedLinkageData.fundId;

        // Check if assignmentId and fundId are present and not empty
        if (!assignmentId || assignmentId === '' || !fundId || fundId === '') {
            console.error('Missing required data:', {
                assignmentId: assignmentId,
                fundId: fundId,
                selectedLinkageData: selectedLinkageData
            });
            setUnlinkAlertType('error');
            setUnlinkAlertMessage('Missing required data to unlink account');
            setUnlinkConfirmModalOpen(false);
            return;
        }

        try {
            setLinkSaving(true);
            setUnlinkAlertType('');
            setUnlinkAlertMessage('');
            setUnlinkConfirmModalOpen(false); // Close confirmation modal
            
            const url = `${Base_URL}/accounting/assignments/${selectedLinkageData.assignmentId}/funds`;
            const payload = {
                fundIds: [selectedLinkageData.fundId]
            };
            
            const res = await ApiCall(url, payload, 'delete');
            const ok = res && (res.statusCode === 200 || res.statusCode === 201 || res.success === true);
            
            if (ok) {
                setUnlinkAlertType('success');
                setUnlinkAlertMessage(res.message || 'Account unlinked successfully');
                
                // Refresh the onboarded investors list
                if (firmId && selectedLinkageData.email) {
                    await dispatch(getOnboardedInvestors({ firmId, email: '' }));
                }
                
                // Close the modal after a short delay
                setTimeout(() => {
                    handleCloseViewLinkageModal();
                }, 1500);
            } else {
                setUnlinkAlertType('error');
                setUnlinkAlertMessage((res && (res.message || res.error)) || 'Failed to unlink account');
            }
        } catch (err) {
            setUnlinkAlertType('error');
            setUnlinkAlertMessage((err && err.message) || 'Failed to unlink account');
        } finally {
            setLinkSaving(false);
        }
    };


    if (showFundDetailsWithManageTab && selectedFund) {
        return (
            <FundDetails
                fund={selectedFund}
                fundId={selectedFund.id}
                firmId={firmId}
                onBack={() => { setShowFundDetailsWithManageTab(false); setSelectedFund(null); }}
                firmName={firmName}
                initialTab={2}
            />
        );
    }

    if (selectedFund) {
        return (
            <FundDetails
                fund={selectedFund}
                fundId={selectedFund.id}
                firmId={firmId}
                onBack={handleBackToFundList}
                firmName={firmName}
            />
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Add back navigation with firm name if onBack is provided */}
            {onBack && (
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <IconButton onClick={onBack} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        NAV
                    </Typography>
                </Box>
            )}
            
            {/* Tab Navigation */}
            <Paper sx={{ mb: 3, borderRadius: '8px', overflow: 'hidden', paddingBottom:"22px !important", p:1.5 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    TabIndicatorProps={{
                        style: {
                            backgroundColor: '#1976d2',
                        }
                    }}
                    sx={{
                        '& .MuiTabs-indicator': {
                            height: 3,
                        },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 'normal',
                            color: 'text.secondary',
                            fontSize: '0.95rem',
                            padding: '16px 24px 20px 24px',
                            minWidth: 'auto',
                        },
                        '& .Mui-selected': {
                            color: '#1976d2',
                            fontWeight: 'medium'
                        }
                    }}
                >
                    <Tab label="Fund List" />
                    <Tab label="Investor List" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && (
                <Box sx={{ backgroundColor: "white", borderRadius: "8px", p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Fund List</Typography>
                
                {funds.length > 0 ? (
                    <>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1.2fr", gap: 1.5, mb: 2 }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>Fund Name</Typography>
                            <Typography variant="body2" sx={{ color: "#666", textAlign: "center" }}>Net ROR: Fund Level</Typography>
                            <Typography variant="body2" sx={{ color: "#666", textAlign: "center" }}>AUM $</Typography>
                            <Typography variant="body2" sx={{ color: "#666", textAlign: "center" }}>Action</Typography>
                        </Box>
                        
                        {getCurrentPageItems().map((fund, index) => (
                    <Box 
                        key={fund.id} 
                        sx={{ 
                            display: "grid", 
                            gridTemplateColumns: "1.2fr 1fr 1fr 1.2fr", 
                            gap: 1.5,
                            py: 1.2,
                            borderBottom: index < getCurrentPageItems().length - 1 ? "1px solid #eee" : "none",
                            alignItems: "center"
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box sx={{ 
                                width: 32, 
                                height: 32, 
                                minWidth: 32,
                                minHeight: 32,
                                borderRadius: "50%", 
                                backgroundColor: "#f0f0f0", 
                                display: "flex", 
                                alignItems: "center", 
                                justifyContent: "center",
                                mr: 1.5
                            }}>
                                <Typography variant="body2" sx={{ textAlign: "center", width: "100%" }}>F{((page - 1) * itemsPerPage) + index + 1}</Typography>
                            </Box>
                            <Typography variant="body1" sx={{ ml: 0 }}>{fund.name}</Typography>
                        </Box>
                        <Typography variant="body1" sx={{ textAlign: "center" }}>{fund.returns ? `${fund.returns.toFixed(2)}%` : "0.00%"}</Typography>
                        <Typography variant="body1" sx={{ textAlign: "center" }}>{fund.aum ? fund.aum.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "0.00"}</Typography>
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                            <Button 
                                variant="text" 
                                sx={{ color: "#1976d2", textTransform: "none" }}
                                onClick={() => handleViewFundDetails(fund)}
                            >
                                See Details
                            </Button>
                            <Button 
                                variant="text" 
                                sx={{ color: "#1976d2", textTransform: "none" }}
                                onClick={() => handleOpenInvestorsModal(fund)}
                            >
                                See Investors
                            </Button>
                        </Box>
                    </Box>
                        ))}
                        
                        {funds.length > itemsPerPage && (
                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                                <Pagination 
                                    count={totalPages} 
                                    page={page} 
                                    onChange={handlePageChange} 
                                    color="primary" 
                                />
                            </Box>
                        )}
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        py: 8,
                        textAlign: 'center'
                    }}>
                        <Box sx={{ 
                            width: 80, 
                            height: 80, 
                            borderRadius: '50%', 
                            backgroundColor: '#f5f5f5',
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            mb: 2
                        }}>
                            <Typography sx={{ fontSize: '40px', color: '#bdbdbd' }}>📁</Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121', mb: 1 }}>
                            No Funds Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#757575', maxWidth: '400px' }}>
                            There are no funds available for this accounting firm.
                        </Typography>
                    </Box>
                )}
                </Box>
            )}

            {/* Investor List Tab */}
            {activeTab === 1 && (
                <Box sx={{ backgroundColor: "white", borderRadius: "8px", p: 3 }}>
                    {/* Search and Actions Bar */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                        <TextField
                            placeholder="Search by name, email, or fund"
                            value={investorSearchTerm}
                            onChange={handleInvestorSearchChange}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "#666", fontSize: "20px" }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ 
                                width: 330,
                                maxWidth: 330,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    height: '40px'
                                }
                            }}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleToggleSortOrder}
                                sx={{
                                    borderColor: '#d0d7de',
                                    color: '#24292f',
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    px: 3,
                                    py: 1,
                                    height: '40px',
                                    fontWeight: 400,
                                    minWidth: 140,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    '&:hover': {
                                        borderColor: '#b0b7be',
                                        backgroundColor: '#f6f8fa'
                                    }
                                }}
                            >
                                <FilterListIcon sx={{ fontSize: '18px' }} />
                                <Typography sx={{ fontSize: '14px' }}>Name</Typography>
                                <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
                                    {investorSortOrder === 'asc' ? '↑' : '↓'}
                                </Typography>
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleOpenLinkageModal}
                                sx={{
                                    backgroundColor: "#1976d2",
                                    color: "white",
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    px: 3,
                                    py: 1,
                                    height: '40px',
                                    fontWeight: 500
                                }}
                            >
                                Create Account Linkage
                            </Button>
                        </Box>
                    </Box>

                    {/* Investor Table */}
                    {investorsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : getCurrentPageInvestors().length > 0 ? (
                        <>
                            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1.5fr 1fr 1fr", gap: 2, mb: 2 }}>
                                <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>Investor Info</Typography>
                                <Typography variant="body2" sx={{ color: "#666", fontWeight: 500, textAlign: "center" }}>Fund</Typography>
                                <Typography variant="body2" sx={{ color: "#666", fontWeight: 500, textAlign: "center" }}>Link Status</Typography>
                                <Typography variant="body2" sx={{ color: "#666", fontWeight: 500, textAlign: "center" }}>Action</Typography>
                            </Box>

                            {getCurrentPageInvestors().map((investor, index) => {
                            const name = investor.name || investor.InvestorName || 'Unknown Investor';
                            const email = investor.email || investor.InvestorEmail || '';
                            const initials = getInitials(name);
                            // Find the fund name from the funds list
                            const fundId = investor.fundId ? String(investor.fundId) : '';
                            const fund = funds.find(f => String(f.id) === fundId);
                            const fundName = fund ? fund.name : (fundId || '—');
                            const investorIdDisplay = investor.investorId ? `(ID: ${investor.investorId})` : '';
                            const isLinked = true; // these rows come from onboarded payload
                            
                            return (
                                <Box 
                                    key={investor.InvestorId || investor.investorId || investor.id || index} 
                                    sx={{ 
                                        display: "grid", 
                                        gridTemplateColumns: "2fr 1.5fr 1fr 1fr", 
                                        gap: 2,
                                        py: 2.5,
                                        borderBottom: index < getCurrentPageInvestors().length - 1 ? "1px solid #eee" : "none",
                                        alignItems: "flex-start"
                                    }}
                                >
                                    {/* Investor Info */}
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                        <Box sx={{ 
                                            width: 40, 
                                            height: 40, 
                                            minWidth: 40,
                                            minHeight: 40,
                                            borderRadius: "50%", 
                                            backgroundColor: "#e0e3e7", 
                                            display: "flex", 
                                            alignItems: "center", 
                                            justifyContent: "center",
                                            flexShrink: 0
                                        }}>
                                            <Typography variant="body2" sx={{ textAlign: "center", width: "100%", color: "#757575", fontWeight: 600, fontSize: "14px" }}>
                                                {initials}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                            <Typography variant="body1" sx={{ fontWeight: 500, color: "#212121", fontSize: "16px", mt: 1}}>
                                                {name} 
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "#757575", fontSize: "14px", lineHeight: 1.2 }}>
                                                {email}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Fund */}
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 1.5 }}>
                                        <Typography variant="body1" sx={{ textAlign: "center", color: "#212121", fontSize: "16px", lineHeight: 1.2, fontWeight: 500 }}>
                                            {fundName}
                                        </Typography>
                                        {investorIdDisplay && (
                                            <Typography variant="body2" sx={{ textAlign: "center", color: "#757575", fontSize: "12px", lineHeight: 1.2, mt: 0.5 }}>
                                                {investorIdDisplay}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Link Status */}
                                    <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
                                        <Box
                                            sx={{
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: "16px",
                                                fontSize: "13px",
                                                fontWeight: 500,
                                                backgroundColor: isLinked ? "#dcfce7" : "#fef2f2",
                                                color: isLinked ? "#166534" : "#dc2626",
                                                display: "inline-block"
                                            }}
                                        >
                                            {isLinked ? "Linked" : "Unlinked"}
                                        </Box>
                                    </Box>

                                    {/* Action */}
                                    <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
                                        {isLinked ? (
                                            <Button
                                                variant="text"
                                                onClick={() => handleViewLinkage(investor)}
                                                sx={{ 
                                                    color: "#1976d2", 
                                                    textTransform: "none",
                                                    fontSize: "14px",
                                                    fontWeight: 500,
                                                    p: 0,
                                                    textDecoration: "none"
                                                }}
                                            >
                                                View Linkage
                                            </Button>
                                        ) : (
                                            <Typography variant="body2" sx={{ color: "#757575", pt: 0.5 }}>{investor.investorId || '-'}</Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                            })}

                            {/* Pagination */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="body2" sx={{ color: "#666", fontSize: "14px" }}>Rows per page:</Typography>
                                    <FormControl size="small" sx={{ minWidth: 80 }}>
                                        <Select
                                            value={investorRowsPerPage}
                                            onChange={handleInvestorRowsPerPageChange}
                                            sx={{ 
                                                minWidth: 60,
                                                height: 32,
                                                '& .MuiSelect-select': {
                                                    padding: '6px 12px',
                                                    fontSize: '14px'
                                                }
                                            }}
                                        >
                                            <MenuItem value={7}>7</MenuItem>
                                            <MenuItem value={10}>10</MenuItem>
                                            <MenuItem value={25}>25</MenuItem>
                                            <MenuItem value={50}>50</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Pagination 
                                    count={Math.ceil(getFilteredInvestors().length / investorRowsPerPage)} 
                                    page={investorPage} 
                                    onChange={handleInvestorPageChange} 
                                    color="primary"
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontSize: '14px',
                                            minWidth: 32,
                                            height: 32
                                        },
                                        '& .MuiPaginationItem-page.Mui-selected': {
                                            backgroundColor: '#1976d2',
                                            color: 'white'
                                        }
                                    }}
                                />
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            py: 8,
                            textAlign: 'center'
                        }}>
                            <Box sx={{ 
                                width: 80, 
                                height: 80, 
                                borderRadius: '50%', 
                                backgroundColor: '#f5f5f5',
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                mb: 2
                            }}>
                                <Typography sx={{ fontSize: '40px', color: '#bdbdbd' }}>👥</Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121', mb: 1 }}>
                                No Investors Found
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#757575', maxWidth: '400px', mb: 3 }}>
                                {investorSearchTerm 
                                    ? `No investors found matching "${investorSearchTerm}". Try a different search term.`
                                    : 'There are no linked investors yet. Click "Create Account Linkage" to get started.'
                                }
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
            {/* Create Account Linkage Modal */}
            <Dialog 
                open={linkageModalOpen} 
                onClose={handleCloseLinkageModal} 
                maxWidth="md" 
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        maxHeight: '100vh',
                        width: '50%'
                    }
                }}
            >
                <Box sx={{ p: 4 }}>
                    {/* Modal Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#212121' }}>
                            Create Account Linkage
                        </Typography>
                        <IconButton onClick={handleCloseLinkageModal} sx={{ color: '#666' }}>
                            <Typography variant="h6">×</Typography>
                        </IconButton>
                    </Box>

                    {/* Instructions */}
                    <Typography variant="body1" sx={{ color: '#666', mb: 2, lineHeight: 1.5 }}>
                        Search for an investor by email to display their registered Prime accounts, then link the NAV accounts to each Prime account.
                    </Typography>

                    {/* Email Search Section */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', mb: 2, fontWeight: 500 }}>
                            Enter investor's email
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <TextField
                                fullWidth
                                placeholder="Enter investor's email"
                                value={searchEmail}
                                onChange={(e) => {
                                    setSearchEmail(e.target.value);
                                    setEmailError('');
                                }}
                                error={!!emailError}
                                helperText={emailError}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        height: '48px'
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                startIcon={<SearchIcon />}
                                onClick={handleSearchInvestor}
                                disabled={!searchEmail.trim() || searchLoading}
                                sx={{
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    px: 3,
                                    py: 1,
                                    height: '48px',
                                    fontWeight: 500,
                                    minWidth: 120
                                }}
                            >
                                {searchLoading ? 'Searching...' : 'Search'}
                            </Button>
                        </Box>
                    </Box>

                    {/* Inline status banner */}
                    {inlineAlertMessage && (
                        <Box sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: '6px',
                            backgroundColor: inlineAlertType === 'success' ? '#e8f5e9' : '#ffebee',
                            color: inlineAlertType === 'success' ? '#2e7d32' : '#c62828',
                            border: `1px solid ${inlineAlertType === 'success' ? '#c8e6c9' : '#ffcdd2'}`,
                            fontSize: '14px'
                        }}>
                            {inlineAlertMessage}
                        </Box>
                    )}

                    {/* Loader while searching investors by email */}
                    {searchLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                        <Box sx={{ 
                            mb: 2,
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            backgroundColor: 'white'
                        }}>
                            {searchResults.map((account, index) => (
                                <Box key={account.id}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center', 
                                        py: 2,
                                        px: 3,
                                        borderBottom: index < searchResults.length - 1 ? '1px solid #eee' : 'none'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#212121' }}>
                                                {account.accountName}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    backgroundColor: account.accountType === 'Individual' ? '#e3f2fd' : 
                                                                   account.accountType === 'Corporate' ? '#e8f5e8' : '#fff3e0',
                                                    color: account.accountType === 'Individual' ? '#1976d2' : 
                                                           account.accountType === 'Corporate' ? '#2e7d32' : '#f57c00',
                                                }}
                                            >
                                                {account.accountType}
                                            </Box>
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Typography sx={{ fontSize: '16px', color: '#1344EB' }}>🔗</Typography>}
                                            onClick={() => handleLinkNAVAccounts(account.id)}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: '8px',
                                                borderRadius: '4px',
                                                color: '#1344EB',
                                                textAlign: 'center',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                lineHeight: '20px',
                                                letterSpacing: '0.1px',
                                                borderColor: '#1344EB',
                                                textTransform: "none",
                                                '&:hover': {
                                                    backgroundColor: '#1344EB',
                                                    color: 'white',
                                                    borderColor: '#1344EB'
                                                }
                                            }}
                                        >
                                            Link NAV Accounts
                                        </Button>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Missing Accounts Section */}
                    {searchResults.length > 0 && (
                        <Box sx={{ 
                            display: 'flex',
                            padding: '12px 16px',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '8px',
                            alignSelf: 'stretch',
                            borderRadius: '4px',
                            border: '1px solid #808080',
                            background: '#F7F7F7',
                            mt: 2
                        }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121' }}>
                                {isCreatingNewAccount ? 'Create New Account' : 'Missing account(s)?'}
                            </Typography>
                            {!isCreatingNewAccount && (
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    Can't find all accounts? Send invite to create account on Prime
                                </Typography>
                            )}
                            {!showCreateAccount ? (
                                <Button
                                    variant="contained"
                                    onClick={handleSendInvite}
                                    fullWidth
                                    sx={{
                                        backgroundColor: "#1976d2",
                                        color: "white",
                                        textTransform: "none",
                                        borderRadius: "8px",
                                        py: 1.5,
                                        height: '48px',
                                        fontWeight: 500
                                    }}
                                >
                                    Send invite
                                </Button>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
                                    <FormControl sx={{ minWidth: 520 }}>
                                        <InputLabel>Account type</InputLabel>
                                        <Select
                                            value={newAccountType}
                                            onChange={handleAccountTypeChange}
                                            label="Account Type"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    height: '40px'
                                                }
                                            }}
                                        >
                                            <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                                            <MenuItem value="CORPORATE">Corporate</MenuItem>
                                            <MenuItem value="TRUST">Trust</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Button
                                        variant="contained"
                                        onClick={handleCreateAccount}
                                        disabled={!newAccountType}
                                        sx={{
                                            backgroundColor: "#1976d2",
                                            color: "white",
                                            textTransform: "none",
                                            borderRadius: "8px",
                                            px: 4,
                                            py: 2,
                                            height: '40px',
                                            fontWeight: 500
                                        }}
                                    >
                                        Create
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* No Data Found UI */}
                    {hasSearched && searchResults.length === 0 && !searchLoading && !showCreateAccount && (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            gap: 3, 
                            p: 4, 
                            textAlign: 'center'
                        }}>
                            <Box sx={{ 
                                width: 60, 
                                height: 60, 
                                borderRadius: '50%', 
                                border: '2px solid #e0e0e0',
                                backgroundColor: 'white', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <Typography sx={{ color: '#9e9e9e', fontWeight: 'normal', fontSize: '24px' }}>i</Typography>
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121', mb: 1, fontSize: '18px' }}>
                                    No Prime accounts under this email
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                                    You can invite or search for another email.
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleClearSearch}
                                    sx={{
                                        borderColor: '#e0e0e0',
                                        color: '#666',
                                        textTransform: "none",
                                        borderRadius: "8px",
                                        px: 3,
                                        py: 1,
                                        height: '40px',
                                        fontWeight: 500
                                    }}
                                >
                                    Clear Search
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleCreateAccountClick}
                                    sx={{
                                        backgroundColor: "#1976d2",
                                        color: "white",
                                        textTransform: "none",
                                        borderRadius: "8px",
                                        px: 3,
                                        py: 1,
                                        height: '40px',
                                        fontWeight: 500
                                    }}
                                >
                                    Create Account
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Create Account Form - Shows when Create Account button is clicked */}
                    {showCreateAccount && hasSearched && searchResults.length === 0 && (
                        <Box 
                            ref={formContainerRef}
                            sx={{ 
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: '12px',
                                alignSelf: 'stretch',
                                borderRadius: '4px',
                                border: '1px solid #808080',
                                background: '#F7F7F7',
                                p: '16px 20px',
                                transition: 'opacity 0.2s ease-in-out'
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121' }}>
                                {isCreatingNewAccount ? 'Create New Account' : 'Missing account(s)?'}
                            </Typography>
                            {!isCreatingNewAccount && (
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    Can't find all accounts? Send invite to create account on Prime
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
                                <FormControl sx={{ minWidth: 520 }}>
                                    <InputLabel>Account type</InputLabel>
                                    <Select
                                        value={newAccountType}
                                        onChange={handleAccountTypeChange}
                                        label="Account type"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                height: '40px'
                                            }
                                        }}
                                    >
                                        <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                                        <MenuItem value="CORPORATE">Corporate</MenuItem>
                                        <MenuItem value="TRUST">Trust</MenuItem>
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="contained"
                                    onClick={handleCreateAccount}
                                    disabled={!newAccountType || createAccountLoading}
                                    sx={{
                                        backgroundColor: createAccountLoading ? "#9e9e9e" : "#1976d2",
                                        color: "white",
                                        textTransform: "none",
                                        borderRadius: "8px",
                                        px: 4,
                                        py: 2,
                                        height: '40px',
                                        fontWeight: 500
                                    }}
                                >
                                    {isCreatingNewAccount ? (createAccountLoading ? 'Processing...' : 'Continue') : 'Create'}
                                </Button>
                            </Box>
                            <Button
                                variant="text"
                                onClick={() => {
                                    setShowCreateAccount(false);
                                    setNewAccountType('');
                                    setIsCreatingNewAccount(false);
                                }}
                                sx={{
                                    color: '#1976d2',
                                    textTransform: "none",
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    p: 0,
                                    mt: 1,
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                ← Back to search options
                            </Button>
                        </Box>
                    )}
                    

                    {/* Initial State - No Search Yet */}
                    {!hasSearched && searchResults.length === 0 && !searchLoading && (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            gap: 2, 
                            p: 4, 
                            textAlign: 'center'
                        }}>
                            <Box sx={{ 
                                width: 40, 
                                height: 40, 
                                borderRadius: '50%', 
                                border: '2px solid #e0e0e0',
                                backgroundColor: 'white', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <Typography sx={{ color: '#9e9e9e', fontWeight: 'normal', fontSize: '18px' }}>i</Typography>
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121', mb: 1, fontSize: '18px' }}>
                                    Enter investor's email
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                                    Enter an investor's email above to view available Prime accounts.
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Dialog>

            {/* Link Accounts Modal */}
            <Dialog 
                open={linkAccountsModalOpen} 
                onClose={handleCloseLinkAccountsModal} 
                maxWidth="md" 
                
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        maxHeight: '90vh',
                        width: '50%',
                        zIndex: 1301 // Higher than the first modal
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.3)' // Semi-transparent backdrop
                    }
                }}
            >
                <Box sx={{ p: 4 }}>
                    {/* Modal Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton onClick={handleCloseLinkAccountsModal} sx={{ color: '#666', p: 0 }}>
                                <Typography variant="h6">←</Typography>
                            </IconButton>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 600, color: '#212121' }}>
                                    Link Accounts
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                                    Link relevant NAV accounts for
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleCloseLinkAccountsModal} sx={{ color: '#666' }}>
                            <Typography variant="h6">×</Typography>
                        </IconButton>
                    </Box>

                    {/* Selected Account Section */}
                    {selectedAccount && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            p: 2,
                            mb: 3,
                            border: '1px solid #e0e0e0'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#212121', fontSize: '15px' }}>
                                    {selectedAccount.accountName}
                                </Typography>
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        backgroundColor: selectedAccount.accountType === 'CORPORATE' ? '#e8f5e8' : '#e3f2fd',
                                        color: selectedAccount.accountType === 'CORPORATE' ? '#2e7d32' : '#1976d2',
                                        textTransform: 'capitalize'
                                    }}
                                >
                                    {(selectedAccount.accountType || 'Individual').toLowerCase()}
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>Linked</Typography>
                                <Box sx={{ 
                                    width: 24, 
                                    height: 24, 
                                    borderRadius: '50%', 
                                    backgroundColor: '#333', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '13px',
                                    fontWeight: 600
                                }}>
                                    {linkedCount}
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Available NAV Accounts Section */}
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121', mb: 2 }}>
                        Select from available NAV Account(s)
                    </Typography>

                    {linkAlertMessage && (
                        <Box sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: '6px',
                            backgroundColor: linkAlertType === 'success' ? '#e8f5e9' : '#ffebee',
                            color: linkAlertType === 'success' ? '#2e7d32' : '#c62828',
                            border: `1px solid ${linkAlertType === 'success' ? '#c8e6c9' : '#ffcdd2'}`,
                            fontSize: '14px'
                        }}>
                            {linkAlertMessage}
                        </Box>
                    )}

                    <Box sx={{ 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '8px', 
                        maxHeight: '300px', 
                        overflowY: 'auto', 
                        mb: 3,
                        backgroundColor: 'white'
                    }}>
                        {/* Render matched NAV accounts from API */}
                        {navMatchesLoading ? (
                            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            (selectedAccount && matchedNavAccounts.length > 0) ? (
                                (() => {
                                   
                                    
                                    // Deduplicate accounts based on fundId:investorId pairs
                                    const uniqueAccountsMap = new Map();
                                    matchedNavAccounts.forEach(acc => {
                                        const fundIdStr = acc.fundId != null ? String(acc.fundId) : '';
                                        const invIdStr = acc.investorId != null ? String(acc.investorId) : '';
                                        const pairKey = fundIdStr && invIdStr ? `${fundIdStr}:${invIdStr}` : '';
                                        if (pairKey && !uniqueAccountsMap.has(pairKey)) {
                                            uniqueAccountsMap.set(pairKey, acc);
                                        }
                                    });
                                    const uniqueAccounts = Array.from(uniqueAccountsMap.values());
                                   
                                    
                                    // No sorting - just display all accounts as-is
                                    
                                    return uniqueAccounts.map((account, index) => {
                                        const key = `${account.fundId}-${account.investorId}-${index}`;
                                        const selKey = `nav-${key}`;
                                        const fundIdStr = account.fundId != null ? String(account.fundId) : '';
                                        const invIdStr = account.investorId != null ? String(account.investorId) : '';
                                        const pairKey = fundIdStr && invIdStr ? `${fundIdStr}:${invIdStr}` : '';
                                        const isLinked = pairKey ? linkedInvestorPairsSet.has(pairKey) : false;
                                        
                                        return (
                                            <Box 
                                                key={key}
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 2, 
                                                    py: 1.5,
                                                    px: 2,
                                                    backgroundColor: 'white',
                                                    borderBottom: index < uniqueAccounts.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                    opacity: isLinked ? 0.5 : 1,
                                                    '&:hover': {
                                                        backgroundColor: isLinked ? 'white' : '#fafafa'
                                                    }
                                                }}
                                            >
                                                <Box sx={{ 
                                                    width: 18, 
                                                    height: 18, 
                                                    border: '2px solid #d0d0d0', 
                                                    borderRadius: '3px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: isLinked ? 'not-allowed' : 'pointer',
                                                    backgroundColor: selectedNavAccounts.includes(selKey) ? '#1976d2' : (isLinked ? '#e0e0e0' : 'white'),
                                                    transition: 'all 0.2s ease',
                                                    flexShrink: 0,
                                                    pointerEvents: isLinked ? 'none' : 'auto'
                                                }}
                                                onClick={() => { if (!isLinked) handleNavAccountToggle(selKey); }}
                                                >
                                                    {selectedNavAccounts.includes(selKey) && !isLinked && (
                                                        <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" sx={{ 
                                                        color: '#212121', 
                                                        fontSize: '14px', 
                                                        fontWeight: 500, 
                                                        lineHeight: 1.4,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {account.name}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ 
                                                        color: '#757575', 
                                                        fontSize: '13px', 
                                                        lineHeight: 1.4,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {account.fund}
                                                    </Typography>
                                                </Box>
                                                {isLinked && (
                                                    <Box sx={{ 
                                                        width: 20, 
                                                        height: 20, 
                                                        borderRadius: '50%',
                                                        backgroundColor: 'transparent',
                                                        border: '1.5px solid #ff9800',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        ml: 1
                                                    }}>
                                                        <Typography sx={{ 
                                                            color: '#ff9800', 
                                                            fontSize: '12px', 
                                                            fontWeight: 'bold',
                                                            lineHeight: 1
                                                        }}>
                                                            i
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    });
                                })()
                            ) : (
                                <Box sx={{ py: 2, textAlign: 'center', color: '#666' }}>No matching NAV accounts</Box>
                            )
                        )}
                    </Box>


                    {/* Save Button */}
                    <Button
                        variant="contained"
                        onClick={handleSaveLinkages}
                        fullWidth
                        disabled={selectedNavAccounts.length === 0 || linkSaving}
                        sx={{
                            backgroundColor: selectedNavAccounts.length > 0 && !linkSaving ? "#1976d2" : "#9e9e9e",
                            color: "white",
                            textTransform: "none",
                            borderRadius: "8px",
                            py: 1.5,
                            height: '48px',
                            fontWeight: 500,
                            '&:disabled': {
                                backgroundColor: "#9e9e9e",
                                color: "white"
                            }
                        }}
                    >
                        {linkSaving ? 'Saving...' : 'Save Linkage(s) & Continue'}
                    </Button>
                </Box>
            </Dialog>

            {/* Investors List Modal */}
            <Dialog open={investorsModalOpen} onClose={handleCloseInvestorsModal} maxWidth="sm" fullWidth>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ color: '#757575', fontWeight: 500, fontSize: '16px' }}>
                            Investor List
                        </Typography>
                        <Button variant="outlined" color="primary" size="medium" sx={{ fontWeight: 500, fontSize: '16px', borderColor: '#1976d2', color: '#1344EB', px: 2, py: 0.5, borderRadius: '6px', textTransform: 'none', boxShadow: 'none' }} onClick={handleOpenManageInvestorsTab}>
                            Manage Investors
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1, color: '#757575', fontWeight: 500, fontSize: '14px' }}>
                            Investor Name
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 0, color: '#757575', fontWeight: 500, fontSize: '14px', minWidth: 110, textAlign: 'right' }}>
                            State
                        </Typography>
                    </Box>
                    <hr style={{ margin: 0, border: 0, borderTop: '1px solid #eee' }} />
                    <Box sx={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden' }}>
                        {modalInvestorsLoading ? (
                            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            (() => {
                                
                                // Filter unique investors based on InvestorId - only show each InvestorId once
                                const uniqueInvestors = [];
                                const seenIds = new Set();
                                
                                modalInvestors.forEach((inv) => {
                                    // Try to find the investor ID from various possible field names
                                    const investorId = inv.InvestorId || inv.investorId || inv.id || inv.investor_id || 
                                                      inv.InvestorID || inv.investorID || inv.ID;
                                    
                                    // Only add if we haven't seen this InvestorId before
                                    if (!seenIds.has(investorId)) {
                                        seenIds.add(investorId);
                                        uniqueInvestors.push(inv);
                                    }
                                });
                                return uniqueInvestors.length === 0 ? (
                                    <Box sx={{ py: 3, textAlign: 'center', color: '#757575' }}>No investors found.</Box>
                                ) : (
                                    uniqueInvestors.map((inv, idx) => {
                                        const name = inv.InvestorName || '';
                                        const initials = getInitials(name);
                                        const onboardLabel = inv.onboarded ? 'Onboarded' : 'Not onboarded';
                                        return (
                                            <Box key={inv.InvestorId || inv.investorId || inv.id || name + idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.2 }}>
                                                <Box 
                                                    sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        flex: 1,
                                                        cursor: 'pointer',
                                                        '&:hover .investor-name': {
                                                            color: '#1976d2',
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                    onClick={handleOpenManageInvestorsTab}
                                                >
                                                    <Box sx={{
                                                        width: 40,
                                                        height: 40,
                                                        minWidth: 40,
                                                        minHeight: 40,
                                                        borderRadius: '25%',
                                                        backgroundColor: '#e0e3e7',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mr: 2
                                                    }}>
                                                        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%', color: '#757575', fontWeight: 600, fontSize: '16px' }}>{initials}</Typography>
                                                    </Box>
                                                    <Typography 
                                                        variant="body1" 
                                                        className="investor-name"
                                                        sx={{ 
                                                            fontWeight: 500, 
                                                            fontSize: '16px', 
                                                            color: '#212121',
                                                            transition: 'color 0.2s, text-decoration 0.2s'
                                                        }}
                                                    >
                                                        {name}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        minWidth: 110,
                                                        textAlign: 'center',
                                                        bgcolor: '#fff8e1',
                                                        color: '#e6a700',
                                                        border: '1px solid #ffe0b2',
                                                        borderRadius: '16px',
                                                        px: 2,
                                                        py: 0.5,
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        display: 'inline-block',
                                                        letterSpacing: 0.1,
                                                    }}
                                                >
                                                    {onboardLabel}
                                                </Box>
                                            </Box>
                                        );
                                    })
                                );
                            })()
                        )}
                    </Box>
                </Box>
            </Dialog>

            {/* View Linkage Modal */}
            <Dialog 
                open={viewLinkageModalOpen} 
                onClose={handleCloseViewLinkageModal}
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        width: '480px',
                        maxWidth: '90vw'
                    }
                }}
            >
                <Box sx={{ p: 3 }}>
                    {/* Modal Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121', fontSize: '20px' }}>
                            Account Linkage
                        </Typography>
                        <IconButton onClick={handleCloseViewLinkageModal} sx={{ color: '#666', p: 0 }}>
                            <Typography variant="h5" sx={{ lineHeight: 1 }}>×</Typography>
                        </IconButton>
                    </Box>

                    {/* Description */}
                    <Typography variant="body2" sx={{ color: '#666', mb: 3, fontSize: '14px' }}>
                        This NAV account is associated with the following Prime account:
                    </Typography>

                    {/* Alert Message */}
                    {unlinkAlertMessage && (
                        <Box sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: '8px',
                            backgroundColor: unlinkAlertType === 'success' ? '#e8f5e9' : '#ffebee',
                            color: unlinkAlertType === 'success' ? '#2e7d32' : '#c62828',
                            border: `1px solid ${unlinkAlertType === 'success' ? '#c8e6c9' : '#ffcdd2'}`,
                            fontSize: '14px',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <Typography sx={{ fontSize: '18px' }}>
                                {unlinkAlertType === 'success' ? '✓' : '⚠'}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                {unlinkAlertMessage}
                            </Typography>
                        </Box>
                    )}

                    {selectedLinkageData && (
                        <>
                            {/* NAV Account Section */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, mb: 1.5, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    NAV Account
                                </Typography>
                                <Box sx={{ 
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    p: 2
                                }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#212121', mb: 0.5, fontSize: '15px' }}>
                                        {selectedLinkageData.navAccount.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
                                        {selectedLinkageData.navAccount.fund}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Prime Account Section */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, mb: 1.5, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Prime Account
                                </Typography>
                                {selectedLinkageData.primeAccounts.map((account, index) => (
                                    <Box 
                                        key={index}
                                        sx={{ 
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px',
                                            p: 2,
                                            mb: index < selectedLinkageData.primeAccounts.length - 1 ? 2 : 0
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#212121', fontSize: '15px' }}>
                                                {account.name}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: '12px',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    backgroundColor: account.type === 'INDIVIDUAL' ? '#e3f2fd' : 
                                                                   account.type === 'CORPORATE' ? '#e8f5e8' : '#fff3e0',
                                                    color: account.type === 'INDIVIDUAL' ? '#1976d2' : 
                                                           account.type === 'CORPORATE' ? '#2e7d32' : '#f57c00',
                                                    textTransform: 'capitalize'
                                                }}
                                            >
                                                {account.type.toLowerCase()}
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCloseViewLinkageModal}
                                    sx={{
                                        borderColor: '#d0d7de',
                                        color: '#24292f',
                                        textTransform: "none",
                                        borderRadius: "6px",
                                        px: 3,
                                        py: 1,
                                        fontWeight: 500,
                                        '&:hover': {
                                            borderColor: '#b0b7be',
                                            backgroundColor: '#f6f8fa'
                                        }
                                    }}
                                >
                                    Close
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleOpenUnlinkConfirmation}
                                    disabled={linkSaving || !selectedLinkageData || !selectedLinkageData.assignmentId || !selectedLinkageData.fundId}
                                    sx={{
                                        backgroundColor: linkSaving ? "#9e9e9e" : "#dc2626",
                                        color: "white",
                                        textTransform: "none",
                                        borderRadius: "6px",
                                        px: 3,
                                        py: 1,
                                        fontWeight: 500,
                                        '&:hover': {
                                            backgroundColor: linkSaving ? "#9e9e9e" : "#b91c1c"
                                        },
                                        '&:disabled': {
                                            backgroundColor: "#9e9e9e",
                                            color: "white"
                                        }
                                    }}
                                >
                                    Unlink
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Dialog>

            {/* Unlink Confirmation Modal */}
            <Dialog 
                open={unlinkConfirmModalOpen} 
                onClose={handleCloseUnlinkConfirmation}
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        width: '400px',
                        maxWidth: '90vw'
                    }
                }}
            >
                <Box sx={{ p: 3 }}>
                    {/* Modal Header */}
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#212121', fontSize: '18px', mb: 2 }}>
                        Unlink Account
                    </Typography>

                    {/* Confirmation Message */}
                    <Typography variant="body2" sx={{ color: '#666', mb: 3, fontSize: '14px', lineHeight: 1.6 }}>
                        Are you sure you want to unlink this account?
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseUnlinkConfirmation}
                            disabled={linkSaving}
                            sx={{
                                borderColor: '#d0d7de',
                                color: '#24292f',
                                textTransform: "none",
                                borderRadius: "6px",
                                px: 3,
                                py: 1,
                                fontWeight: 500,
                                '&:hover': {
                                    borderColor: '#b0b7be',
                                    backgroundColor: '#f6f8fa'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleUnlinkAccount}
                            disabled={linkSaving}
                            sx={{
                                backgroundColor: linkSaving ? "#9e9e9e" : "#dc2626",
                                color: "white",
                                textTransform: "none",
                                borderRadius: "6px",
                                px: 3,
                                py: 1,
                                fontWeight: 500,
                                '&:hover': {
                                    backgroundColor: linkSaving ? "#9e9e9e" : "#b91c1c"
                                },
                                '&:disabled': {
                                    backgroundColor: "#9e9e9e",
                                    color: "white"
                                }
                            }}
                        >
                            {linkSaving ? 'Unlinking...' : 'Unlink'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default FundList;